"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Flame, Skull, Sparkles, ArrowUpRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import UserHoverCard from "@/app/components/UserHoverCard";
import GradientAvatar from "@/app/components/GradientAvatar";
import { useT } from "@/hooks/useT";
import { fmtCents } from "@/lib/format-price";
import { decodeHTMLEntities } from "@/lib/sanitize";
import CopyPauseButton from "../shared/CopyPauseButton";
import ProbabilityBar from "../shared/ProbabilityBar";
import { useMarketData } from "@/hooks/useMarketData";
import { formatUSD, timeAgo, displayTraderName, abbreviateOutcomeLabel } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// Whale Trade Card — twitter-style 3-line density.
//
// Replaces the prior heavy layout (badge row + trader row + full
// PolymarketMarketCard embed + actions row → ~250px tall) with:
//   L1: avatar | trader · bought YES · @ 35¢ · $275K          (action + size)
//       tier · 5h                                              (meta line)
//   L2: ↗ Market title (one-row context, links to /event)
//   L3: 🔥 · 💀                              [Copy @user]      (actions)
//
// Fresh-wallet variant differs only in the tier badge color + sublabel
// ("First trade on Polymarket"); same structure otherwise.
export default function WhaleTradeRow({
  event,
  onCopy,
  onVote,
  followedTraders,
  onToggleCopy,
}: {
  event: ActivityEvent;
  onCopy: (event: ActivityEvent) => void;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
  followedTraders: Map<string, { leaderId: string; enabled: boolean }>;
  onToggleCopy: (wallet: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const headline = event.headline?.toLowerCase() || "";
  let whaleTier = "fish";
  if (headline.includes("mega whale")) whaleTier = "mega_whale";
  else if (headline.includes("whale") && !headline.includes("mega")) whaleTier = "whale";
  else if (headline.includes("dolphin")) whaleTier = "dolphin";
  else if (headline.includes("fish")) whaleTier = "fish";
  else whaleTier = event.metadata?.whale_tier || event.event_subtype || "fish";

  const tierLabels: Record<string, string> = {
    mega_whale: t("activity.tier.mega_whale"),
    whale: t("activity.tier.whale"),
    dolphin: t("activity.tier.dolphin"),
    fish: t("activity.tier.fish"),
  };
  const tierLabel = tierLabels[whaleTier] || t("activity.tier.fish");

  const isBuy = event.side === "BUY";
  const isYes = event.outcome?.toLowerCase().includes('yes');
  const isFreshWhale = event.event_subtype === "fresh_whale" || event.metadata?.is_fresh_wallet === true;

  // Trader display name: username, wallet name, or truncated hash — never
  // the raw headline, which contains marketing prefixes like "🐋 WHALE:".
  // wallet_name from metadata can itself be the raw 0x… address when the
  // wallet has no label, so we run it through displayTraderName to truncate.
  const traderName = displayTraderName(
    event.source_username
    || event.metadata?.wallet_name
    || event.source_wallet
  );
  const traderLink = `/traders/${event.source_wallet || event.metadata?.wallet_address || ''}`;

  // Market context one-liner — title + thumbnail + arrow link. Avoid the
  // 100-line PolymarketMarketCard embed: it duplicates the price/outcome
  // info already in the trader line and inflates the card 3-4×.
  const marketTitle = event.market_title || decodeHTMLEntities(event.headline || '').replace(/^[^:]+:\s*/, '');
  const marketHref = event.event_slug
    ? `/event/${event.event_slug}`
    : event.market_slug
      ? `/market/${event.market_slug}`
      : null;

  // Outcome buttons (Spurs 64.5¢ / Timberwolves 35.5¢) reconstruct from
  // event.outcome + event.price + market_title (head-to-head) or fall back
  // to Yes/No for binary markets. Same logic as ActivityDetailClient; kept
  // local here to avoid per-row API fetches that would pile up across a
  // long feed. Live prices are still surfaced via the detail page click-
  // through.
  let buttonOutcomes: string[] = [];
  let buttonPrices: number[] = [];
  let buttonLabels: string[] = [];
  if (event.price != null && event.outcome) {
    const rawP = Number(event.price);
    const tradedProb = rawP > 1 ? rawP / 100 : rawP;
    if (tradedProb >= 0 && tradedProb <= 1) {
      const outcomeLower = event.outcome.toLowerCase().trim();
      const titleHasVs = !!marketTitle && / vs\.? /i.test(marketTitle);
      if (titleHasVs) {
        const sides = marketTitle.split(/ vs\.? /i).map(s => s.trim().replace(/\?$/, ""));
        if (sides.length === 2) {
          const idx = sides.findIndex(s => s.toLowerCase() === outcomeLower);
          if (idx >= 0) {
            buttonOutcomes = sides;
            buttonPrices = idx === 0 ? [tradedProb, 1 - tradedProb] : [1 - tradedProb, tradedProb];
          }
        }
      }
      if (buttonOutcomes.length === 0 && (outcomeLower === "yes" || outcomeLower === "no")) {
        buttonOutcomes = ["Yes", "No"];
        buttonPrices = outcomeLower === "yes" ? [tradedProb, 1 - tradedProb] : [1 - tradedProb, tradedProb];
      }
    }
  }
  // Abbreviation from slug for sports head-to-head ("nba-sas-min-..." → SAS/MIN).
  buttonLabels = buttonOutcomes.slice();
  if (buttonOutcomes.length === 2 && event.market_slug && / vs\.? /i.test(marketTitle || '')) {
    const parts = event.market_slug.split("-");
    const leaguePrefixes = new Set(["nba", "nfl", "mlb", "nhl", "epl", "laliga", "seriea", "ucl", "ncaaf", "ncaab", "f1", "atp", "wta", "mls", "mma", "ufc"]);
    const tokens: string[] = [];
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (i === 0 && leaguePrefixes.has(p)) continue;
      if (/^\d{2,4}$/.test(p)) break;
      if (p.length >= 2 && p.length <= 4 && /^[a-z]+$/i.test(p)) tokens.push(p.toUpperCase());
    }
    if (tokens.length >= 2) buttonLabels = [tokens[0], tokens[1]];
  }
  buttonLabels = buttonLabels.map(abbreviateOutcomeLabel);

  // Tier-specific badge classes — color-coded bg + border + text in the
  // tier's accent. Drops the previous "dot + plain label" pattern: when
  // the timeAgo became a badge, the tier label needed matching weight to
  // keep the meta line visually balanced.
  const tierBadge = isFreshWhale
    ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
    : whaleTier === 'mega_whale' || whaleTier === 'whale'
      ? "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
      : whaleTier === 'dolphin'
        ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400"
        : "bg-gray-500/10 border-gray-500/20 text-gray-600 dark:text-gray-400";

  // Live market metadata (volume, liquidity, endDate) — TanStack Query
  // dedupes across rows showing the same slug, and the backend caches 60s,
  // so the per-row cost is bounded even on long feeds.
  const { data: liveMarketData } = useMarketData(event.market_slug, {
    enabled: !!event.market_slug,
    source: 'polymarket',
  });
  const marketVolume = liveMarketData?.volume ?? null;
  const marketLiquidity = liveMarketData?.liquidity ?? null;
  const marketEndDate = liveMarketData?.endDate ?? null;
  const marketClosed = liveMarketData?.closed === true;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    router.push(`/activity/${event.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={CARD_BASE}
    >
      <div className="py-2.5 px-1">
        {/* L1: avatar + trader action + amount */}
        <div className="flex items-center gap-2.5">
          <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
            <Link href={traderLink} prefetch={false} className="flex-shrink-0">
              {event.metadata?.wallet_profile_image ? (
                <CdnImage src={event.metadata.wallet_profile_image} alt={traderName} width={32} height={32} sizes="32px" className="w-8 h-8 rounded-[5px] object-cover" />
              ) : (
                <GradientAvatar seed={traderName} size={32} />
              )}
            </Link>
          </UserHoverCard>
          <div className="flex-1 min-w-0">
            {/* Action line — left: trader prose; right: market context link.
                Market sits in the slot the dollar amount used to occupy so
                the right side of the card stays anchored with useful data
                instead of going empty. Truncates on narrow viewports. */}
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-1.5 flex-wrap min-w-0">
                <UserHoverCard username={event.source_username || undefined} walletAddress={event.source_wallet || undefined}>
                  <Link href={traderLink} prefetch={false} className="text-sm font-bold text-gray-900 dark:text-white hover:underline truncate">
                    {traderName}
                  </Link>
                </UserHoverCard>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
                  isBuy
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400'
                }`}>
                  {isBuy ? t("activity.action.bought") : t("activity.action.sold")}
                </span>
                {event.size_usd != null && (
                  <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatUSD(event.size_usd)}
                  </span>
                )}
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
                  isYes
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400'
                }`}>
                  {event.outcome}
                </span>
                {event.price != null && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    @ <span className="font-semibold text-gray-900 dark:text-white">{fmtCents(Number(event.price))}¢</span>
                  </span>
                )}
              </div>
              {/* Timestamp badge — top-right of the action line. Copy
                  moved to the footer (bottom-right). */}
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {timeAgo(event.event_at, t)}
              </span>
            </div>
            {/* Meta line: tier badge (timeAgo moved up to top-right) */}
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`inline-flex items-center gap-0.5 px-1 py-px rounded-[4px] border text-[9px] font-semibold whitespace-nowrap ${tierBadge}`}>
                {isFreshWhale && <Sparkles className="w-2 h-2" />}
                {isFreshWhale ? t("activity.tier.fresh") : tierLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Slim event card — mirrors the market summary inside the post
            detail page: thumbnail + title + ↗ on the right + outcome
            buttons below. No volume/liquidity/endDate here (would require
            a per-row fetch across the feed); the detail page picks those
            up on click-through. */}
        {marketHref && marketTitle && (
          <Link
            href={marketHref}
            prefetch={false}
            onClick={(e) => e.stopPropagation()}
            className="block mt-2.5 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              {event.market_image && (
                <div className="w-8 h-8 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-white/[0.06]">
                  <CdnImage src={event.market_image} alt={marketTitle} width={32} height={32} sizes="32px" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {marketTitle}
                </h3>
                {/* Meta line — source badge · $vol · $liq · endDate. Only
                    the pieces we have land; partial rows still read cleanly. */}
                {(marketVolume != null || marketLiquidity != null || marketEndDate) && (() => {
                  const src = (event.metadata?.source as string) || 'polymarket';
                  // Source logo — same assets the SourceSelector uses. Empty
                  // logo (predict.fun) renders as a "P" text chip fallback.
                  const sourceLogo =
                    src === 'kalshi' ? '/logos/kalshi/k-icon.svg'
                    : src === 'hyperliquid' ? '/logos/hyperliquid/symbol-mint.svg'
                    : src === 'predict' ? ''
                    : '/logos/polymarket/icon-white.png';
                  const sourceLetter = src === 'predict' ? 'P' : '';
                  const sourceBg =
                    src === 'kalshi' ? 'bg-emerald-500/15'
                    : src === 'predict' ? 'bg-violet-500/15'
                    : src === 'hyperliquid' ? 'bg-cyan-500/15'
                    : 'bg-blue-500/15';
                  return (
                  <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-[4px] flex-shrink-0 ${sourceBg}`}>
                      {sourceLogo ? (
                        <img src={sourceLogo} alt={src} className="w-3.5 h-3.5 object-contain" />
                      ) : (
                        <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400">{sourceLetter}</span>
                      )}
                    </span>
                    {marketClosed && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">
                        {t("activity.badge.marketClosed")}
                      </span>
                    )}
                    {marketVolume != null && marketVolume > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketVolume)} vol</span>
                      </>
                    )}
                    {marketLiquidity != null && marketLiquidity > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketLiquidity)} liq</span>
                      </>
                    )}
                    {marketEndDate && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap">
                          {new Date(marketEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </>
                    )}
                  </div>
                  );
                })()}
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
            {buttonOutcomes.length === 2 && (
              <div className="flex gap-1.5 mt-2">
                {[0, 1].map((i) => {
                  const isEmerald = i === 0;
                  const borderColor = isEmerald ? 'border-emerald-500/30 hover:border-emerald-500/60' : 'border-red-500/30 hover:border-red-500/60';
                  const bgColor = isEmerald ? 'bg-emerald-500/5 hover:bg-emerald-500/10' : 'bg-red-500/5 hover:bg-red-500/10';
                  const textColor = isEmerald ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
                  return (
                    <Link
                      key={buttonOutcomes[i]}
                      href={`${marketHref}?outcome=${encodeURIComponent(buttonOutcomes[i].toLowerCase())}`}
                      prefetch={false}
                      onClick={(e) => e.stopPropagation()}
                      className={`flex-1 flex items-center justify-between px-2.5 py-1.5 rounded-[5px] border transition-colors ${borderColor} ${bgColor}`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wide truncate ${textColor}`}>
                        {buttonLabels[i]}
                      </span>
                      <span className="text-xs font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0 ml-2">
                        {(buttonPrices[i] * 100).toFixed(1)}¢
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </Link>
        )}

        {/* Footer: votes (flush left) + Copy (flush right). */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onVote(event.id, 1, 'activity_event')}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                event.user_vote === 1 ? 'text-orange-500 bg-orange-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-orange-500'
              }`}
            >
              <Flame className="w-3.5 h-3.5" />
              <span>{event.reactions?.fire || event.likes || 0}</span>
            </button>
            <button
              onClick={() => onVote(event.id, -1, 'activity_event')}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                event.user_vote === -1 ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-red-500'
              }`}
            >
              <Skull className="w-3.5 h-3.5" />
              <span>{event.reactions?.skull || event.dislikes || 0}</span>
            </button>
          </div>
          <CopyPauseButton
            wallet={event.source_wallet || event.metadata?.wallet_address || ''}
            username={displayTraderName(
              event.source_username
              || event.metadata?.display_name
              || event.metadata?.username
              || event.metadata?.wallet_name
              || event.source_wallet
            )}
            avatar={event.metadata?.wallet_profile_image || event.metadata?.user_image}
            followedTraders={followedTraders}
            onToggleCopy={onToggleCopy}
            onCopy={() => onCopy(event)}
          />
        </div>

      </div>
    </motion.div>
  );
}
